import { FIRST_AID } from '../resourcesData'
import type { FirstAidContent } from '../../../hooks/useResourceOverrides'

// First Aid tab — prototype L3763-3782. Oxblood-soft Medical Emergency
// callout, defibrillator-locations table, first-aid-kit paragraph.
//
// All copy is PUBLIC CONTRACT (engineering lessons §10) — defaults come
// from FIRST_AID in resourcesData.ts; never paraphrase inline.
//
// `content` optional prop wires up the per-browser preview/tuning
// surface; when omitted, falls back to the constants directly.

interface Props {
  content?: FirstAidContent
}

export function FirstAidTab({ content }: Props) {
  const resolved: FirstAidContent = content ?? {
    callout: { title: FIRST_AID.callout.title, body: FIRST_AID.callout.body },
    defibLocations: FIRST_AID.defibLocations.map((d) => ({
      location: d.location, where: d.where
    })),
    firstAidKits: FIRST_AID.firstAidKits
  }

  return (
    <div>
      <h3 className="font-display text-xl font-semibold mb-3">First Aid & Defibrillators</h3>

      <div className="bg-oxblood-soft border border-oxblood rounded-lg p-3.5 mb-4">
        <div className="font-semibold text-oxblood mb-1">{resolved.callout.title}</div>
        <div className="text-sm text-ink-soft leading-snug whitespace-pre-wrap">
          {resolved.callout.body}
        </div>
      </div>

      <h4 className="text-xs uppercase font-bold text-ink-soft tracking-wider mb-2">
        Defibrillator Locations
      </h4>
      <div className="overflow-x-auto mb-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-line">
              <th className="text-left py-1.5 pr-3 font-semibold text-xs uppercase tracking-wider text-ink-soft">
                Location
              </th>
              <th className="text-left py-1.5 font-semibold text-xs uppercase tracking-wider text-ink-soft">
                Where to find it
              </th>
            </tr>
          </thead>
          <tbody>
            {resolved.defibLocations.map((d, i) => (
              <tr key={`${i}:${d.location}`} className="border-b border-line/60 last:border-b-0">
                <td className="py-1.5 pr-3 font-semibold align-top">{d.location}</td>
                <td className="py-1.5 align-top leading-snug">{d.where}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 className="text-xs uppercase font-bold text-ink-soft tracking-wider mt-4 mb-2">
        First Aid Kits
      </h4>
      <p className="text-sm leading-snug whitespace-pre-wrap">{resolved.firstAidKits}</p>
    </div>
  )
}
